/**
 * usePolygonState - Polygon data and editing state management
 *
 * Manages all polygon-related states including:
 * - Polygon data from API
 * - Selection states (single and multi)
 * - Editing states (vertex, smooth edit)
 * - Snap edge configuration
 * - Azimuth settings
 */

import { useState, useCallback } from 'react';
import type { PolygonData } from '../../../types/geotiff.types';

export interface PolygonEditState {
  selectedPolygonForEdit: number | null;  // Smooth edit mode (drag/rotate/resize)
  editingPolygonVertex: number | null;     // Vertex edit mode (double-click)
  editedPolygonCoords: { [polygonId: number]: [number, number][] };
  originalPolygonCoords: { [polygonId: number]: [number, number][] };
}

export interface SnapEdgeState {
  snapEdgeIndex: { [polygonId: number]: number };
  snapEdgeAzimuth: { [polygonId: number]: number };
  isSelectingSnapEdge: boolean;
  manualAzimuth: { [polygonId: number]: number | null };
}

export interface PolygonState extends PolygonEditState, SnapEdgeState {
  // Core polygon data
  polygonData: PolygonData | null;
  originalPolygonData: PolygonData | null;
  showPolygons: boolean;

  // Selection
  selectedPolygons: number[];
  activeRoofId: number | null;
}

export interface PolygonStateActions {
  // Core data setters
  setPolygonData: React.Dispatch<React.SetStateAction<PolygonData | null>>;
  setOriginalPolygonData: React.Dispatch<React.SetStateAction<PolygonData | null>>;
  setShowPolygons: (show: boolean) => void;

  // Selection setters
  setSelectedPolygons: React.Dispatch<React.SetStateAction<number[]>>;
  setActiveRoofId: (id: number | null) => void;

  // Edit state setters
  setSelectedPolygonForEdit: (id: number | null) => void;
  setEditingPolygonVertex: (id: number | null) => void;
  setEditedPolygonCoords: React.Dispatch<React.SetStateAction<{ [polygonId: number]: [number, number][] }>>;
  setOriginalPolygonCoords: React.Dispatch<React.SetStateAction<{ [polygonId: number]: [number, number][] }>>;

  // Snap edge setters
  setSnapEdgeIndex: React.Dispatch<React.SetStateAction<{ [polygonId: number]: number }>>;
  setSnapEdgeAzimuth: React.Dispatch<React.SetStateAction<{ [polygonId: number]: number }>>;
  setIsSelectingSnapEdge: (selecting: boolean) => void;
  setManualAzimuth: React.Dispatch<React.SetStateAction<{ [polygonId: number]: number | null }>>;

  // Convenience methods
  togglePolygonSelection: (polygonId: number) => void;
  clearPolygonSelection: () => void;
  clearEditState: () => void;
  startVertexEdit: (polygonId: number) => void;
  stopVertexEdit: () => void;
}

export function usePolygonState(): PolygonState & PolygonStateActions {
  // Core polygon data
  const [polygonData, setPolygonData] = useState<PolygonData | null>(null);
  const [originalPolygonData, setOriginalPolygonData] = useState<PolygonData | null>(null);
  const [showPolygons, setShowPolygons] = useState(true);

  // Selection
  const [selectedPolygons, setSelectedPolygons] = useState<number[]>([]);
  const [activeRoofId, setActiveRoofId] = useState<number | null>(null);

  // Edit state
  const [selectedPolygonForEdit, setSelectedPolygonForEdit] = useState<number | null>(null);
  const [editingPolygonVertex, setEditingPolygonVertex] = useState<number | null>(null);
  const [editedPolygonCoords, setEditedPolygonCoords] = useState<{ [polygonId: number]: [number, number][] }>({});
  const [originalPolygonCoords, setOriginalPolygonCoords] = useState<{ [polygonId: number]: [number, number][] }>({});

  // Snap edge state
  const [snapEdgeIndex, setSnapEdgeIndex] = useState<{ [polygonId: number]: number }>({});
  const [snapEdgeAzimuth, setSnapEdgeAzimuth] = useState<{ [polygonId: number]: number }>({});
  const [isSelectingSnapEdge, setIsSelectingSnapEdge] = useState(false);
  const [manualAzimuth, setManualAzimuth] = useState<{ [polygonId: number]: number | null }>({});

  // Convenience methods
  const togglePolygonSelection = useCallback((polygonId: number) => {
    setSelectedPolygons(prev => {
      if (prev.includes(polygonId)) {
        return prev.filter(id => id !== polygonId);
      }
      return [...prev, polygonId];
    });
  }, []);

  const clearPolygonSelection = useCallback(() => {
    setSelectedPolygons([]);
    setActiveRoofId(null);
  }, []);

  const clearEditState = useCallback(() => {
    setSelectedPolygonForEdit(null);
    setEditingPolygonVertex(null);
  }, []);

  const startVertexEdit = useCallback((polygonId: number) => {
    setEditingPolygonVertex(polygonId);
    setSelectedPolygonForEdit(null);
  }, []);

  const stopVertexEdit = useCallback(() => {
    setEditingPolygonVertex(null);
  }, []);

  return {
    // State
    polygonData,
    originalPolygonData,
    showPolygons,
    selectedPolygons,
    activeRoofId,
    selectedPolygonForEdit,
    editingPolygonVertex,
    editedPolygonCoords,
    originalPolygonCoords,
    snapEdgeIndex,
    snapEdgeAzimuth,
    isSelectingSnapEdge,
    manualAzimuth,

    // Setters
    setPolygonData,
    setOriginalPolygonData,
    setShowPolygons,
    setSelectedPolygons,
    setActiveRoofId,
    setSelectedPolygonForEdit,
    setEditingPolygonVertex,
    setEditedPolygonCoords,
    setOriginalPolygonCoords,
    setSnapEdgeIndex,
    setSnapEdgeAzimuth,
    setIsSelectingSnapEdge,
    setManualAzimuth,

    // Convenience methods
    togglePolygonSelection,
    clearPolygonSelection,
    clearEditState,
    startVertexEdit,
    stopVertexEdit,
  };
}

export default usePolygonState;
